/**
 * Property photographs at the size the layout actually draws them.
 *
 * WHAT THIS IS FOR
 * ----------------
 * The upload pipeline has always made two files: a 2000px "full" for the
 * gallery and a 480px "thumb" for grids. The classic templates used
 * `thumb_url || url` and got the small one. The Estate templates asked for
 * `url` everywhere, so a 333x250 property card was painted with a 2000px
 * photograph — about 1.2MB of it per page. Mobile LCP measured 5.4s on the
 * agency home page and 5.0s on an area page, against a 2.5s "good"
 * threshold and 4.0s "poor".
 *
 * `media/storage.py` now writes a ladder beside the full size (400, 800,
 * 1200) and records the widths on the image. This turns that into the
 * `srcset` the browser needs to pick one.
 *
 * HOW THE URLS WORK
 * -----------------
 * The widths list ends with the full size, whose file is `image.url`
 * itself. Every smaller width is the same key with the width spliced in
 * before the extension, matching how the thumbnail is already named:
 *
 *     abc123.webp          <- the full size, the last entry in `widths`
 *     abc123.400.webp      <- a variant
 *     abc123.thumb.webp    <- the pre-existing 480px thumbnail
 *
 * Deriving the URLs here rather than sending one per width per image keeps
 * a listing payload from carrying thirty-two near-identical strings.
 *
 * ANYTHING UPLOADED BEFORE THE LADDER has no `widths` and gets a plain
 * `src`, exactly as it does today. Backfilling is `scripts/backfill_image_
 * variants.py`; until it has run for a tenant, their pages are no worse
 * than they were.
 */
import { mediaUrl } from "@/lib/api";

/** `abc123.webp` + 400 -> `abc123.400.webp`. */
export function variantUrl(url, width) {
  const s = String(url || "");
  if (!s) return "";
  const i = s.lastIndexOf(".webp");
  if (i === -1) return s;
  return `${s.slice(0, i)}.${width}.webp`;
}

/**
 * The `srcset` for an image record, or undefined when it has no ladder.
 *
 * Undefined rather than a one-entry srcset on purpose: React omits the
 * attribute entirely, which is what an image with nothing to choose from
 * should have.
 */
export function imageSrcSet(image) {
  const widths = (image && image.widths) || [];
  if (!Array.isArray(widths) || widths.length < 2) return undefined;
  const full = Math.max(...widths);
  return widths
    .slice()
    .sort((a, b) => a - b)
    .map((w) => `${mediaUrl(w === full ? image.url : variantUrl(image.url, w))} ${w}w`)
    .join(", ");
}

/**
 * What to put in `sizes`, per layout.
 *
 * `sizes` is a promise about how wide the image will be drawn, and the
 * browser picks from `srcset` before it has laid anything out — so getting
 * it wrong is not cosmetic. Too small and a retina card is soft; too large
 * and the whole exercise is undone. These mirror the widths in
 * foundationSites.css, and the media queries are the ones the CSS uses.
 */
export const SIZES = {
  /** Cards in `.grid.cols-4` — a column each on a phone, four up at full width. */
  card: "(max-width: 700px) 100vw, (max-width: 1100px) 50vw, 340px",
  /** The feature card in the same grid, which spans two columns. */
  cardFeature: "(max-width: 700px) 100vw, (max-width: 1100px) 100vw, 680px",
  /** Gallery tiles on a property page. */
  gallery: "(max-width: 900px) 100vw, 50vw",
  /** A hero or the lightbox: as wide as the screen, whatever the screen is. */
  full: "100vw",
};
